import { Component, OnInit, Input, ElementRef, ViewChild } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { includes, remove, find } from 'lodash';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent, MatDialogRef, MatDialog } from '@angular/material';
import { switchMap, debounceTime, tap, finalize } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import { SystemService } from '../systems/system.service';
import { SymptomService } from '../symptoms/symptom.service';
import { VisitService } from './visit.service';
import { parseErrors } from '../shared/api.service';
import { Note } from '../notes/note';
import { DiagnosisService } from '../diagnoses/diagnosis.service';
import { Diagnosis } from '../diagnoses/diagnosis';
import { NurseVisitDialogComponent } from '../nurse-visits/nurse-visit-dialog.component';
import { NurseVisit } from '../nurse-visits/nurse-visit';
import { ConfirmDialogComponent } from '../shared/dialogs/confirm-dialog.component';
import { NurseVisitService } from '../nurse-visits/nurse-visit.service';

@Component({
  selector: 'visit-details',
  templateUrl: 'visit-details.component.html',
  styleUrls: ['./visit-details.component.scss']
})

export class VisitDetailsComponent implements OnInit {
  @Input() visit;
  @Input() selectedDiagnoses = [];
  errors = [];
  systems = [];
  systemsReq;
  symptoms = [];
  symptomsReq;
  visitReq;
  loading = false;
  diagnosisLoading = false;
  diagnosisCtrl: FormControl;
  diagnoses = [];
  diagnosisReq;
  billingCodes = [
    '99201', '99202', '99203', '99204', '99205', '99211', '99212', '99213',
    '99214', '99215'
  ];
  referNurseDialog: MatDialogRef<any>;
  confirmCloseDialog: MatDialogRef<any>;
  @ViewChild('diagnosesInput') diagnosesInput: ElementRef;
  separatorKeysCodes = [ENTER, COMMA];
  nurseVisitReq: Subscription;

  referNurseCallback = () => {
    this.visit.status = 'nurse_requested';
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private elementRef: ElementRef,
    private visitService: VisitService,
    private nurseVisitService: NurseVisitService,
    private diagnosisService: DiagnosisService,
    private systemService: SystemService,
    private symptomService: SymptomService,
    public dialog: MatDialog
  ) {
    this.diagnosisCtrl = new FormControl();
    this.diagnosisReq = this.diagnosisCtrl.valueChanges.pipe(
      debounceTime(300),
      tap(() => this.diagnosisLoading = true),
      switchMap(value => this.diagnosisService.getDiagnoses({ terms: value }).pipe(
        finalize(() => this.diagnosisLoading = false),
      ))
    ).subscribe(data => this.diagnoses = data);
  }

  ngOnInit() {
    this.getSystems();
    this.getSymptoms();
  }

  submit(close = false, referNurse = false) {
    this.loading = true;
    if (this.visitReq) { this.visitReq.unsubscribe(); }
    let _visit = this.visit;
    if (close) { _visit = {...this.visit, ...{ status: 'closed' } }; }
    _visit.diagnoses = this.selectedDiagnoses;
    this.visitReq = this.visitService.save(_visit).subscribe(visit => {
      this.visit = visit;
      let noteFields = this.elementRef.nativeElement.querySelectorAll('.new-note');
      noteFields.forEach(field => {
        field.value = '';
      });
      this.loading = false;
      if (close) { this.router.navigate(['/queue']); };
      if (referNurse) { this.referNurse(this.visit); }
    }, err => {
      this.loading = false;
      this.errors = parseErrors(err);
    });
  }

  getSymptoms() {
    this.loading = true;
    if (this.symptomsReq) { this.symptomsReq.unsubscribe(); }
    this.symptomsReq = this.symptomService.getSymptoms().subscribe(symptoms => {
      this.symptoms = symptoms;
      this.loading = false;
    }, err => {
      this.loading = false;
      this.errors = parseErrors(err);
    });
  }

  symptomSelected(key) {
    if (this.visit && this.visit.symptoms) {
      let selectedKeys = this.visit.symptoms.map((symptom) => { return symptom.key; });
      return includes(selectedKeys, key);
    } else {
      return false;
    }
  }

  changeSymptom(symptom, event) {
    if (event.target.checked) {
      this.visit.symptoms.push(symptom);
    } else {
      remove(this.visit.symptoms, { key: symptom.key });
    }
  }

  getSystems() {
    this.loading = true;
    if (this.systemsReq) { this.systemsReq.unsubscribe(); }
    this.systemsReq = this.systemService.getSystems().subscribe(systems => {
      this.systems = systems;
      this.loading = false;
    }, err => {
      this.loading = false;
      this.errors = parseErrors(err);
    });
  }

  systemSelected(key) {
    if (this.visit && this.visit.systems) {
      let selectedKeys = this.visit.systems.map((system) => { return system.key; });
      return includes(selectedKeys, key);
    } else {
      return false;
    }
  }

  changeSystem(system, event) {
    if (event.target.checked) {
      this.visit.systems.push(system);
    } else {
      remove(this.visit.systems, { key: system.key });
    }
  }

  changeNote(noteType, event) {
    let note = find(this.visit.newNotes, { noteType: noteType });
    if (note) {
      note['note'] = event.target.value;
    } else {
      note = new Note({ noteType: noteType, note: event.target.value });
      this.visit.newNotes.push(note);
    }
  }

  displayFn(diagnosis: Diagnosis) {
    if (diagnosis) { return diagnosis.name; }
  }

  removeDiagnosis(diagnosis: any): void {
    let index = this.selectedDiagnoses.indexOf(diagnosis);

    if (index >= 0) {
      this.selectedDiagnoses.splice(index, 1);
    }
  }

  selectedDiagnosis(event: MatAutocompleteSelectedEvent): void {
    this.selectedDiagnoses.push(event.option.value);
    this.diagnosesInput.nativeElement.value = '';
    this.diagnosisCtrl.setValue(null);
  }

  referNurse(visit): void {
    this.nurseVisitReq = this.nurseVisitService.save(new NurseVisit({ visit: visit.id })).subscribe(nurseVisit => {
      this.referNurseDialog = this.dialog.open(NurseVisitDialogComponent, {
        width: '680px',
        height: '490px'
      });
      this.referNurseDialog.componentInstance.callback = this.referNurseCallback;
      this.referNurseDialog.componentInstance.model = nurseVisit;
      this.referNurseDialog.afterClosed().subscribe(dialogResult => {
        if (dialogResult) {
          //
        }
        this.referNurseDialog = null;
      });
    }, err => {
      this.loading = false;
      this.errors = parseErrors(err);
    });
  }

  closeVisit(): void {
    this.confirmCloseDialog = this.dialog.open(ConfirmDialogComponent, {
      width: '680px',
      height: '280px'
    });
    this.confirmCloseDialog.afterClosed().subscribe(dialogResult => {
      if (dialogResult) {
        this.submit(true);
      }
      this.confirmCloseDialog = null;
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/visits/visit-details.component.ts